/**
 * Impact/Effort Matrix tool runner.
 * Categorizes items into quadrants: Quick Wins, Big Bets, Fill-ins, Avoid.
 */

import type {
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
} from "../harmonizer/types";
import type { NativeScoreSet } from "../harmonizer/testHarness";
import type { RawToolSpec } from "../harmonizer/specLoader";
import { loadToolSpecFile, getToolSpecPath } from "./specLoader";

let cachedSpec: RawToolSpec | null = null;

/**
 * Gets the raw tool spec for Impact/Effort Matrix.
 */
export function getImpactEffortSpec(): RawToolSpec {
  if (cachedSpec) {
    return cachedSpec;
  }

  const specPath = getToolSpecPath("impact-effort-matrix");
  cachedSpec = loadToolSpecFile(specPath);
  return cachedSpec;
}

/**
 * Runs the Impact/Effort Matrix tool.
 * 
 * @param items - Items to categorize
 * @param _criteriaSet - CriteriaSet (not used, but can contain impact/effort criteria)
 * @param toolConfig - Tool configuration (scale_max, midpoint_threshold)
 * @param lens - Optional lens for filtering
 * @returns Native score set
 */
export async function runImpactEffortTool(
  items: Item[],
  _criteriaSet: CriteriaSet | null,
  toolConfig: ToolConfig,
  lens?: Lens | null
): Promise<NativeScoreSet> {
  const toolRunId = `impact-effort-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Get config values
  const scaleMax = (toolConfig.scale_max as number) || 5;
  const midpointThreshold = (toolConfig.midpoint_threshold as number) ?? scaleMax / 2;

  // Filter items if lens is provided
  let filteredItems = items;
  if (lens) {
    const { applyLens } = await import("../arrange/validation");
    filteredItems = applyLens(items, lens);
  }

  // Quadrant definitions
  const quadrantRanks: Record<string, number> = {
    "quick-wins": 1,
    "big-bets": 2,
    "fill-ins": 3,
    "avoid": 4,
  };

  const categorizedItems: Array<{
    item_id: string;
    quadrant: string;
    quadrant_rank: number;
    impact: number;
    effort: number;
  }> = [];
  const itemsWithQuadrant: Array<{
    item: Item;
    impact: number;
    effort: number;
    quadrant: string;
    quadrantRank: number;
  }> = [];

  for (const item of filteredItems) {
    const impact = item.attributes.impact;
    const effort = item.attributes.effort;

    // Validate required attributes
    if (
      impact === null ||
      impact === undefined ||
      (typeof impact === "number" && (impact < 0 || impact > scaleMax))
    ) {
      continue; // Skip invalid items
    }
    if (
      effort === null ||
      effort === undefined ||
      (typeof effort === "number" && (effort < 0 || effort > scaleMax))
    ) {
      continue;
    }

    const impactNum = typeof impact === "number" ? impact : Number(impact);
    const effortNum = typeof effort === "number" ? effort : Number(effort);

    // Determine quadrant
    const isHighImpact = impactNum >= midpointThreshold;
    const isHighEffort = effortNum >= midpointThreshold;

    let quadrant: string;
    if (isHighImpact && !isHighEffort) {
      quadrant = "quick-wins";
    } else if (isHighImpact && isHighEffort) {
      quadrant = "big-bets";
    } else if (!isHighImpact && !isHighEffort) {
      quadrant = "fill-ins";
    } else {
      quadrant = "avoid";
    }

    itemsWithQuadrant.push({
      item,
      impact: impactNum,
      effort: effortNum,
      quadrant,
      quadrantRank: quadrantRanks[quadrant],
    });
  }

  // Sort by quadrant rank (ascending), then by label (ascending) for tie-breaking
  itemsWithQuadrant.sort((a, b) => {
    if (a.quadrantRank !== b.quadrantRank) {
      return a.quadrantRank - b.quadrantRank;
    }
    return a.item.label.localeCompare(b.item.label);
  });

  // Build output
  for (const { item, impact, effort, quadrant, quadrantRank } of itemsWithQuadrant) {
    categorizedItems.push({
      item_id: item.id,
      quadrant: quadrant,
      quadrant_rank: quadrantRank,
      impact: impact,
      effort: effort,
    });
  }

  return {
    items: categorizedItems,
    toolRunId,
    metadata: {
      total_items: filteredItems.length,
      valid_items: categorizedItems.length,
      midpoint_threshold_used: midpointThreshold,
      scale_max: scaleMax,
    },
  };
}


